import type { Ownership } from '@gnosis/shared';

/** Distingue erreur réseau (bootstrap) et erreur applicative (charte §5). */
export class NetworkError extends Error {}
export class ApiErr extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function send<T>(method: 'POST' | 'PATCH', url: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new NetworkError('network');
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const code = (data?.error?.code as string) ?? 'INTERNAL';
    throw new ApiErr(code, data?.error?.message ?? 'error');
  }
  return data as T;
}
const post = <T>(url: string, body: unknown) => send<T>('POST', url, body);
const patch = <T>(url: string, body: unknown) => send<T>('PATCH', url, body);

/** DELETE (corps vide, 204 attendu). */
async function del(url: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, { method: 'DELETE' });
  } catch {
    throw new NetworkError('network');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new ApiErr((data?.error?.code as string) ?? 'INTERNAL', data?.error?.message ?? 'error');
  }
}

/** POST multipart (pièces jointes). Le navigateur fixe lui-même le Content-Type. */
async function postForm<T>(url: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { method: 'POST', body: form });
  } catch {
    throw new NetworkError('network');
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiErr((data?.error?.code as string) ?? 'INTERNAL', data?.error?.message ?? 'error');
  return data as T;
}

export interface EntityRef {
  name: string;
  type: string;
  ownership: Ownership;
}
export interface Source {
  n: number;
  id: string;
  title: string;
  lang?: string;
  excerpt: string;
  themes: string[];
  entities: EntityRef[];
  capturedBy?: string;
  capturedById?: string;
}
export interface ChatResponse {
  answer: string;
  sources: Source[];
  model: string | null;
  gap?: boolean;
  logId?: string | null;
}

// --- Retours & tableau de bord (D) ---
export interface Insights {
  counts: { total: number; gaps: number; up: number; down: number };
  recentGaps: { id: string; question: string; createdAt: string; sources: number }[];
  top: { question: string; n: number }[];
}

export interface ThemeRef {
  theme: { label: string };
}
export interface AttachmentRef {
  id: string;
  filename: string;
  mime: string;
  size: number;
  extracted: boolean;
}
export interface CaptureResponse {
  id: string;
  title: string;
  bodyClean: string;
  status: 'ACTIVE' | 'PENDING_ENRICH' | 'ARCHIVED' | 'MERGED';
  createdAt?: string;
  themes: ThemeRef[];
  entities: { entity: { name: string; type: { label: string }; ownership: Ownership } }[];
  redactions: { kind: string; count: number }[];
  attachments?: AttachmentRef[];
}

/** Page de captures d'un auteur (Tes captures / Capturé par X) + total hors-page. */
export interface AuthorNotes {
  items: CaptureResponse[];
  total: number;
}

export interface Status {
  version: string;
  ai: boolean;
  spend: { today: number; month: number; total: number };
  budget: { monthly: number | null; remaining: number | null; low: boolean };
}

/** Construit la query-string de pagination/recherche des captures d'un auteur. */
function authorQuery(opts: { q?: string; limit?: number; offset?: number }): string {
  const p = new URLSearchParams();
  if (opts.q?.trim()) p.set('q', opts.q.trim());
  if (opts.limit != null) p.set('limit', String(opts.limit));
  if (opts.offset != null) p.set('offset', String(opts.offset));
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function get<T>(url: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url);
  } catch {
    throw new NetworkError('network');
  }
  if (!res.ok) throw new ApiErr('INTERNAL', 'error');
  return (await res.json()) as T;
}

// --- Explorer (navigation) ---
export interface ThemeRow {
  id: string;
  label: string;
  n: number;
}
export interface TypeRow {
  type: string;
  n: number;
}
export interface OwnRow {
  ownership: Ownership;
  n: number;
}
export interface EntityRow {
  id: string;
  name: string;
  type: string;
  ownership: Ownership;
  n: number;
}
export interface GrainCard {
  id: string;
  title: string;
  excerpt: string;
  createdAt: string;
}
export interface Overview {
  stats: { grains: number; entities: number; themes: number; docs: number };
  topThemes: ThemeRow[];
  topTypes: TypeRow[];
  ownership: OwnRow[];
}
export interface ThemeDetail {
  kind: 'theme';
  theme: { id: string; label: string; sheet: string | null };
  grains: GrainCard[];
  entities: EntityRow[];
  relatedThemes: ThemeRow[];
}
export interface EntityDetail {
  kind: 'entity';
  entity: { id: string; name: string; type: string; ownership: Ownership; sheet: string | null };
  grains: GrainCard[];
  themes: ThemeRow[];
  relatedEntities: EntityRow[];
}
export interface GrainDetail {
  id: string;
  title: string;
  lang?: string;
  bodyClean: string;
  createdAt: string;
  themes: { theme: { id: string; label: string } }[];
  entities: { entity: { id: string; name: string; type: { label: string }; ownership: Ownership } }[];
  attachments?: AttachmentRef[];
}

// --- Sources ingérées (disclaimer + aide) ---
export interface SourceGroup {
  name: string;
  docs: number;
}
export interface SourceSummary {
  totals: { docs: number; grains: number };
  sharepoint: SourceGroup[];
  teams: SourceGroup[];
  web: SourceGroup[];
}

// --- Gnosis proactif : questions ouvertes (PRO-01) ---
export interface OpenQuestion {
  id: string;
  kind: 'entity' | 'theme';
  refId: string;
  subject: string;
  question: string;
  tag?: string;
}
export interface ProactiveResult {
  questions: OpenQuestion[];
  generatedAt: number;
}
export interface SheetSourceRef {
  n: number;
  id: string;
  title: string;
  excerpt: string;
}
export interface Chronicle {
  digest: string;
  sources: SheetSourceRef[];
  generatedAt: number;
}
export interface ContradictionItem {
  id: string;
  subject: string;
  entityId: string;
  explanation: string;
  a: { id: string; title: string; excerpt: string };
  b: { id: string; title: string; excerpt: string };
}
export interface Contradictions {
  items: ContradictionItem[];
  generatedAt: number;
}

// --- Authentification M365 (AUTH) ---
export interface Me {
  id: string;
  email: string;
  displayName: string;
  role: 'ADMIN' | 'MEMBER';
  canImport: boolean;
  oid: string | null;
}
export interface AuthState {
  user: Me | null;
  oidcConfigured: boolean;
}

/** Source d'un import + corps du job (console étage 2). */
export type ImportSourceBody =
  | { type: 'web'; url: string; depth?: number; max?: number }
  | { type: 'sharepoint'; url: string }
  | { type: 'teams'; team: string; channel?: string; docs?: boolean };
/** Pas de champ `enrich` : l'enrichissement IA est systématique côté serveur. */
export interface ImportJobBody {
  mode: 'estimate' | 'run';
  source: ImportSourceBody;
  limit?: number;
}
export interface ImportJob {
  id: string;
  mode: 'estimate' | 'run';
  sourceType: 'web' | 'sharepoint' | 'teams';
  status: 'running' | 'done' | 'error';
  scope: string | null;
  total: number;
  done: number;
  docs: number;
  grains: number;
  redactions: number;
  skipped: number;
  costUsd: number | null;
  error: string | null;
}

/** Ligne « accès & rôles » (panneau admin). `pending` = invité, pas encore connecté. */
export interface AccessUser {
  id: string;
  email: string;
  displayName: string;
  admin: boolean;
  canImport: boolean;
  pending: boolean;
  createdAt: string;
}

const qs = (params: Record<string, string | undefined>) =>
  Object.entries(params)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v!)}`)
    .join('&');

export const api = {
  ask: (question: string, deep = false) =>
    post<ChatResponse>('/api/chat', { question, ...(deep ? { deep: true } : {}) }),
  chatFeedback: (logId: string, value: 1 | -1) => post<{ ok: boolean }>('/api/chat/feedback', { logId, value }),
  insights: () => get<Insights>('/api/admin/insights'),
  // Accès & rôles (roue dentée admin).
  admin: {
    users: (q?: string) => get<AccessUser[]>(`/api/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    invite: (email: string, flags: { admin?: boolean; canImport?: boolean }) =>
      post<AccessUser>('/api/admin/users', { email, ...flags }),
    setAccess: (id: string, flags: { admin?: boolean; canImport?: boolean }) =>
      patch<AccessUser>(`/api/admin/users/${encodeURIComponent(id)}`, flags),
  },
  // Console d'import (étage 2, Importateurs).
  import: {
    graphStatus: () => get<{ connected: boolean }>('/api/import/graph-status'),
    teams: () => get<{ id: string; displayName: string }[]>('/api/import/teams'),
    channels: (teamId: string) =>
      get<{ id: string; displayName: string }[]>(`/api/import/teams/${encodeURIComponent(teamId)}/channels`),
    createJob: (input: ImportJobBody) => post<ImportJob>('/api/import/jobs', input),
    job: (id: string) => get<ImportJob>(`/api/import/jobs/${encodeURIComponent(id)}`),
  },
  capture: (text: string, tags?: string[], files?: File[]) => {
    if (files && files.length) {
      const form = new FormData();
      form.append('text', text);
      if (tags && tags.length) form.append('tags', JSON.stringify(tags));
      files.forEach((f) => form.append('files', f));
      return postForm<CaptureResponse>('/api/notes', form);
    }
    return post<CaptureResponse>('/api/notes', { text, ...(tags && tags.length ? { tags } : {}) });
  },
  attachmentUrl: (noteId: string, attId: string) => `/api/notes/${noteId}/attachments/${attId}`,
  // Réédition d'un grain capturé → ré-enrichissement IA (CAP-05).
  updateCapture: (id: string, text: string, tags?: string[]) =>
    patch<CaptureResponse>(`/api/notes/${id}`, { text, ...(tags && tags.length ? { tags } : {}) }),
  // « Tes captures » côté serveur (grains dont tu es l'auteur, suit l'identité).
  // « Tes captures » paginées + recherche par mot-clé (AUTH / CHAT-06).
  myNotes: (opts: { q?: string; limit?: number; offset?: number } = {}) =>
    get<AuthorNotes>(`/api/notes/mine${authorQuery(opts)}`),
  // « Capturé par X » : tout ce qu'une personne a capturé (clic sur la pastille).
  authorNotes: (id: string, opts: { q?: string; limit?: number; offset?: number } = {}) =>
    get<AuthorNotes & { author: { id: string; displayName: string } }>(
      `/api/notes/by-author/${encodeURIComponent(id)}${authorQuery(opts)}`,
    ),
  // Suppression d'une de tes captures (CAP-07).
  deleteNote: (id: string) => del(`/api/notes/${encodeURIComponent(id)}`),
  // Traduction à la volée vers la langue de l'interface (TRAD). Non stockée.
  translate: (text: string, target: 'fr' | 'de') => post<{ text: string }>('/api/translate', { text, target }),
  // Repli : lot de grains par ids conservés côté navigateur (mode ouvert / hors connexion).
  mine: (ids: string[]) =>
    ids.length ? get<CaptureResponse[]>(`/api/notes?ids=${ids.map(encodeURIComponent).join(',')}`) : Promise.resolve([]),
  status: () => get<Status>('/api/status'),
  sources: () => get<SourceSummary>('/api/sources/summary'),
  proactive: {
    questions: () => get<ProactiveResult>('/api/proactive/questions'),
    refresh: () => post<ProactiveResult>('/api/proactive/refresh', {}),
    chronicle: () => get<Chronicle>('/api/proactive/chronicle'),
    chronicleRefresh: () => post<Chronicle>('/api/proactive/chronicle/refresh', {}),
    contradictions: () => get<Contradictions>('/api/proactive/contradictions'),
    contradictionsRefresh: () => post<Contradictions>('/api/proactive/contradictions/refresh', {}),
    dismissQuestion: (id: string) => post<{ ok: boolean }>('/api/proactive/dismiss', { kind: 'question', id }),
    dismissContradiction: (id: string) => post<{ ok: boolean }>('/api/proactive/dismiss', { kind: 'contradiction', id }),
  },
  auth: {
    me: () => get<AuthState>('/api/auth/me'),
    logout: () => post<{ ok: boolean }>('/api/auth/logout', {}),
    loginUrl: '/api/auth/login', // navigation pleine page (redirige vers Entra)
  },
  browse: {
    overview: () => get<Overview>('/api/browse/overview'),
    themes: (q?: string) => get<ThemeRow[]>(`/api/themes?${qs({ q, limit: '40' })}`),
    entities: (q?: string, type?: string) => get<EntityRow[]>(`/api/entities?${qs({ q, type, limit: '40' })}`),
    theme: (id: string) => get<ThemeDetail>(`/api/themes/${id}`),
    entity: (id: string) => get<EntityDetail>(`/api/entities/${id}`),
    grain: (id: string) => get<GrainDetail>(`/api/notes/${id}`),
    synthEntity: (id: string) => post<{ sheet: string }>(`/api/entities/${id}/synthesize`, {}),
    synthTheme: (id: string) => post<{ sheet: string }>(`/api/themes/${id}/synthesize`, {}),
  },
};
